import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings, pick } from '@/contexts/SiteSettingsContext';
import { Phone, Mail, MessageCircle, Clock, Send, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MaintenancePage() {
  const { language } = useLanguage();
  const { settings } = useSiteSettings();
  const isAr = language === 'ar';
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const phone = pick(settings?.phone, '+971 50 123 4567');
  const whatsapp = pick(settings?.whatsapp, '+971522898249');
  const cEmail = pick(settings?.email, 'hello@gmreae.com');
  const hours = isAr ? pick(settings?.business_hours_ar, 'الأحد - الخميس · 9 ص - 6 م') : pick(settings?.business_hours_en, 'Sun - Thu · 9 AM - 6 PM');
  const maintMsg = isAr
    ? pick(settings?.maintenance_message_ar, 'نعمل حالياً على تطوير تجربة Guleed Mortgage لتقدّم لكم استشارات الرهن العقاري على أعلى مستوى. عودتنا قريباً.')
    : pick(settings?.maintenance_message_en, 'We are crafting an elevated Guleed Mortgage experience to deliver premium mortgage advisory. We will be back shortly.');
  const eta = isAr ? pick(settings?.maintenance_eta_ar, 'العودة المرتقبة قريباً') : pick(settings?.maintenance_eta_en, 'Relaunch imminent');

  const notify = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email, source: 'maintenance_page' });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    finally { setLoading(false); }
  };

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-screen relative overflow-hidden bg-[#0B2F44] text-white" data-testid="maintenance-page">
      {/* Ambient backdrop */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(166,137,102,0.18) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(201,168,76,0.12) 0%, transparent 50%)'
        }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
          backgroundSize: '64px 64px'
        }} />
      </div>

      {/* Vertical edge mark */}
      <div className={`hidden lg:flex absolute ${isAr ? 'left-8' : 'right-8'} top-0 bottom-0 z-10 items-center pointer-events-none`}>
        <p className="text-[10px] tracking-[0.6em] uppercase text-[#A68966]/60" style={{ writingMode: 'vertical-rl', transform: isAr ? 'rotate(180deg)' : 'none' }}>
          GULEED · ABU DHABI · 2026
        </p>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="flex items-center justify-between px-6 sm:px-12 lg:px-20 py-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#A68966] to-[#C9A84C] flex items-center justify-center font-bold text-[#0B2F44]" style={{ fontFamily: "'Playfair Display', serif" }}>G</div>
            <div>
              <p className="text-sm font-bold tracking-wider" style={{ fontFamily: "'Playfair Display', serif" }}>Guleed Mortgage</p>
              <p className="text-[9px] text-[#A68966] uppercase tracking-[0.3em]">{isAr ? 'استشارات الرهن العقاري' : 'Mortgage Advisory'}</p>
            </div>
          </Link>
          <Link to="/login" className="text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-[#A68966] transition" data-testid="maintenance-admin-link">
            {isAr ? 'دخول الإدارة' : 'Admin Login'}
          </Link>
        </header>

        {/* Hero content */}
        <main className="flex-1 flex items-center px-6 sm:px-12 lg:px-20 pb-12">
          <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 space-y-8">
              {/* Overline */}
              <div className="flex items-center gap-3">
                <span className="w-12 h-px bg-[#A68966]"></span>
                <p className="text-[10px] tracking-[0.5em] uppercase text-[#A68966] font-bold">{eta}</p>
              </div>

              {/* Title */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isAr ? (<>الموقع <span className="italic text-[#C9A84C]">قيد التطوير</span></>) : (<>Under <span className="italic text-[#C9A84C]">Refinement</span></>)}
              </h1>

              {/* Ornament */}
              <div className="flex items-center gap-2">
                <span className="w-8 h-px bg-[#A68966]/40"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#A68966] rotate-45"></span>
                <span className="w-16 h-px bg-[#A68966]/40"></span>
              </div>

              {/* Message */}
              <p className="text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl">{maintMsg}</p>

              {/* Notify form */}
              {!submitted ? (
                <form onSubmit={notify} className="max-w-md mt-4" data-testid="maintenance-notify-form">
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#A68966] mb-3">{isAr ? 'أعلمني عند الإطلاق' : 'Notify me at launch'}</p>
                  <div className="flex backdrop-blur-xl bg-white/5 border border-white/15 rounded-full overflow-hidden shadow-2xl">
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={isAr ? 'بريدك الإلكتروني' : 'your@email.com'} className="flex-1 bg-transparent px-5 py-3.5 text-sm outline-none placeholder:text-white/30" data-testid="maintenance-email" />
                    <button type="submit" disabled={loading} className="bg-[#A68966] hover:bg-[#C9A84C] disabled:opacity-50 text-[#0B2F44] px-5 sm:px-7 text-xs uppercase tracking-[0.2em] font-bold transition flex items-center gap-2" data-testid="maintenance-submit">
                      <Send className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{loading ? (isAr ? '...' : '...') : (isAr ? 'إرسال' : 'Notify')}</span>
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center gap-3 backdrop-blur-xl bg-white/5 border border-[#A68966]/40 rounded-full px-5 py-3.5 max-w-md" data-testid="maintenance-success">
                  <CheckCircle2 className="w-5 h-5 text-[#C9A84C]" />
                  <p className="text-sm text-white/90">{isAr ? 'شكراً، سنخبرك فور الإطلاق.' : 'Thank you. We will reach out at launch.'}</p>
                </div>
              )}
            </div>

            {/* Contact panel */}
            <aside className="lg:col-span-2 lg:ps-12 lg:border-s lg:border-white/10">
              <p className="text-[10px] tracking-[0.5em] uppercase text-[#A68966] font-bold mb-6">{isAr ? 'تواصل مباشر' : 'Direct Contact'}</p>
              <div className="space-y-4">
                <ContactCard icon={Phone} label={isAr ? 'هاتف' : 'Phone'} value={phone} href={`tel:${phone.replace(/\s/g, '')}`} testId="maint-phone" />
                <ContactCard icon={MessageCircle} label="WhatsApp" value={whatsapp} href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}`} testId="maint-whatsapp" />
                <ContactCard icon={Mail} label={isAr ? 'بريد إلكتروني' : 'Email'} value={cEmail} href={`mailto:${cEmail}`} testId="maint-email" />
                <ContactCard icon={Clock} label={isAr ? 'ساعات العمل' : 'Hours'} value={hours} testId="maint-hours" />
              </div>
            </aside>
          </div>
        </main>

        {/* Footer */}
        <footer className="px-6 sm:px-12 lg:px-20 py-6 border-t border-white/10 flex items-center justify-between text-[10px] text-white/30 uppercase tracking-[0.3em]">
          <p>© {new Date().getFullYear()} Guleed Mortgages Real Estate LLC</p>
          <p className="hidden sm:block">{isAr ? 'مرخص في الإمارات' : 'Licensed in UAE'}</p>
        </footer>
      </div>
    </div>
  );
}

const ContactCard = ({ icon: Icon, label, value, href, testId }) => {
  const Inner = (
    <>
      <div className="w-10 h-10 rounded-full bg-[#A68966]/15 border border-[#A68966]/30 flex items-center justify-center shrink-0 group-hover:bg-[#A68966] group-hover:border-[#A68966] transition">
        <Icon className="w-4 h-4 text-[#A68966] group-hover:text-[#0B2F44] transition" />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] uppercase tracking-[0.3em] text-white/40">{label}</p>
        <p className="text-sm text-white/90 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
      </div>
    </>
  );
  return href ? (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="group flex items-center gap-4 backdrop-blur-xl bg-white/[0.04] border border-white/10 rounded-2xl p-4 hover:border-[#A68966]/50 hover:bg-white/[0.07] transition" data-testid={testId}>
      {Inner}
    </a>
  ) : (
    <div className="group flex items-center gap-4 backdrop-blur-xl bg-white/[0.04] border border-white/10 rounded-2xl p-4" data-testid={testId}>
      {Inner}
    </div>
  );
};
